import { InstanciaPaso, PeriodoProceso } from "../types/instanciaPaso";
import { NOMBRE_SEMESTRE } from "./semestre";

/**
 * Agrupa los procesos visibles para profesor/revisor en las dos pestañas de la
 * vista staff: los estudiantes que acompaño (guío o coguío) y las revisiones que
 * me tocan. Se calcula en el frontend a partir de los flags de rol del serializer.
 *
 * Guiar y coguiar iban en pestañas separadas y era una división artificial: en las
 * dos uno acompaña a un estudiante y lo que se quiere ver es la lista completa.
 * Ahora van juntos y cada fila dice con qué rol.
 *
 * El **semestre** cambia qué se muestra, no solo cuánto:
 *
 * - Sin semestre: lo que está en curso, más lo que se aprobó en el semestre
 *   vigente. Aprobar un paso no saca al estudiante de la lista del guía mientras
 *   dure el período: es lo último que pasó y el guía lo quiere ver.
 * - Con semestre: todo lo de ese semestre, terminado o no. Es la forma de ir a
 *   buscar a los estudiantes de años anteriores.
 *
 * En "Mis estudiantes" la fila es siempre por persona (su etapa más avanzada del
 * periodo mirado): si aprobó dos pasos en el mismo semestre sale una vez, con el
 * último. En "Revisiones" es por proceso, que es la unidad que se evalúa.
 *
 * El **backend ya filtró** por semestre antes de mandar las filas (ver
 * `useProcesosStaff`): esto reparte y ordena lo que llegó, no recorta un histórico
 * completo. Por eso los semestres del selector vienen aparte (`opcionesSemestre`):
 * si se dedujeran de estas filas, solo se podría elegir el semestre ya elegido.
 */

const FINALIZADOS = new Set(["aprobado", "reprobado"]);

export type RolEnProceso = "guia" | "coguia";

export interface FilaEstudiante {
  estudianteId: number;
  rut: number | null; // para enlazar a sus procesos
  estudiante: string;
  foto: string | null;
  roles: RolEnProceso[]; // con qué sombrero: guía, coguía, o los dos en distintos procesos
  tema: string | null;
  temaId: number | null; // para enlazar a la ficha del tema
  etapa: string; // paso de la última instancia
  estado: string; // código de estado de esa instancia
  estadoDisplay: string;
  periodo: string; // "2025.2", para ordenar y mostrar cuando se mira el historial
  periodoDisplay: string; // "2025 Primavera", va bajo la etapa
}

export interface AgrupacionStaff {
  mios: FilaEstudiante[]; // estudiantes que guío o coguío
  revisiones: InstanciaPaso[]; // procesos donde integro la comisión
  porRevisar: number; // de esas, cuántas puedo y aún no evalúo (el número de la pestaña y del menú)
}

export interface OpcionSemestre {
  valor: string; // "2025.2"
  label: string; // "2025 Primavera"
}

const periodoDe = (i: InstanciaPaso) => `${i.ano}.${i.semestre}`;

/** Me tocó en la comisión, pero el estudiante todavía no entrega: no hay nada que evaluar. */
export const sinEntregar = (i: InstanciaPaso) => i.estado === "pendiente";

/** Lo que de verdad espera mi evaluación: entregado y sin evaluar por mí. */
export const porEvaluar = (i: InstanciaPaso) => !i.ya_evalue && !sinEntregar(i);

// La "última etapa" de un estudiante = su instancia más avanzada (año, semestre, id).
function ultima(insts: InstanciaPaso[]): InstanciaPaso {
  return [...insts].sort((a, b) => b.ano - a.ano || b.semestre - a.semestre || b.id - a.id)[0];
}

/**
 * Opciones del selector, a partir de los semestres que el backend dice que existen.
 *
 * Se rellenan los huecos: van todos los semestres entre el más antiguo y el más
 * reciente, aunque alguno haya quedado vacío. Elegir uno y no ver a nadie también es
 * una respuesta, y una lista con saltos se lee como si faltaran años.
 */
export function opcionesSemestre(periodos: PeriodoProceso[]): OpcionSemestre[] {
  if (!periodos.length) return [];
  // Semestre como número corrido: ano*2 + sem, con sem 1 (Otoño) o 2 (Primavera).
  const corrido = (ano: number, sem: number) => ano * 2 + sem;
  let min = Infinity;
  let max = -Infinity;
  const extras = new Map<string, OpcionSemestre>();
  for (const p of periodos) {
    if (p.semestre === 1 || p.semestre === 2) {
      min = Math.min(min, corrido(p.ano, p.semestre));
      max = Math.max(max, corrido(p.ano, p.semestre));
    } else {
      // Anual y Verano existen pero son raros: no generan rango, se listan tal cual.
      const valor = `${p.ano}.${p.semestre}`;
      extras.set(valor, { valor, label: `${p.ano} ${p.semestre_display}` });
    }
  }
  const salida: OpcionSemestre[] = [];
  for (let n = max; n >= min; n--) {
    const sem = n % 2 === 1 ? 1 : 2;
    const ano = (n - sem) / 2;
    salida.push({ valor: `${ano}.${sem}`, label: `${ano} ${NOMBRE_SEMESTRE[sem]}` });
  }
  return [...extras.values(), ...salida];
}

/** El semestre más reciente del selector ("2026.2"), o "" si no hay ninguno. */
export function periodoVigente(semestres: OpcionSemestre[]): string {
  const clave = (v: string) => v.split(".").map(Number);
  let max = "";
  for (const { valor } of semestres) {
    const [a, s] = clave(valor);
    const [ma, ms] = max ? clave(max) : [-Infinity, -Infinity];
    if (a > ma || (a === ma && s > ms)) max = valor;
  }
  return max;
}

// Sin semestre elegido: lo abierto, o lo que se cerró en el semestre vigente —da lo
// mismo si aprobó o reprobó: sigue siendo lo último que le pasó este semestre—.
function enCurso(i: InstanciaPaso, vigente: string): boolean {
  if (!FINALIZADOS.has(i.estado)) return true;
  return !!vigente && periodoDe(i) === vigente;
}

// Una fila por estudiante, con el rol con que participo en sus procesos.
// `vigente` null = se mira un semestre elegido y entra todo lo que llegó.
function filasPorEstudiante(insts: InstanciaPaso[], vigente: string | null): FilaEstudiante[] {
  const porEstudiante = new Map<number, InstanciaPaso[]>();
  insts.forEach((i) => {
    if (i.estudiante_id == null) return;
    const arr = porEstudiante.get(i.estudiante_id) ?? [];
    arr.push(i);
    porEstudiante.set(i.estudiante_id, arr);
  });

  const filas: FilaEstudiante[] = [];
  porEstudiante.forEach((arr, id) => {
    // Sin semestre elegido interesa lo que está en curso; con semestre, todo lo de ahí.
    // La fila se arma con lo que califica, así la etapa que se muestra es la última de
    // eso y no un proceso viejo que llegó por otra vía (p. ej. uno que evalué).
    const vivos = vigente === null ? arr : arr.filter((i) => enCurso(i, vigente));
    if (!vivos.length) return;
    const u = ultima(vivos);
    const roles: RolEnProceso[] = [];
    if (arr.some((i) => i.soy_guia)) roles.push("guia");
    if (arr.some((i) => i.soy_coguia)) roles.push("coguia");
    filas.push({
      estudianteId: id,
      rut: u.estudiante_rut,
      estudiante: u.estudiante,
      foto: u.estudiante_foto,
      roles,
      tema: u.tema,
      temaId: u.tema_id,
      etapa: u.paso,
      estado: u.estado,
      estadoDisplay: u.estado_display,
      periodo: periodoDe(u),
      periodoDisplay: `${u.ano} ${u.semestre_display}`,
    });
  });
  return filas.sort((a, b) => a.estudiante.localeCompare(b.estudiante));
}

/** `vigente` es el semestre actual ("2026.2"): sin semestre elegido, lo aprobado ahí
 *  sigue en "Mis estudiantes". */
export function agruparStaff(instancias: InstanciaPaso[], semestre = "", vigente = ""): AgrupacionStaff {
  // El filtro por semestre lo hizo el backend; este se queda como red de seguridad
  // mientras llega la tanda nueva (React Query deja a la vista la del semestre anterior).
  const delPeriodo = semestre ? instancias.filter((i) => periodoDe(i) === semestre) : instancias;

  // Sin semestre: lo que tengo que revisar ahora, lo que me va a tocar cuando el
  // estudiante entregue (ya estoy en su comisión), o lo que ya revisé (para volver).
  // Con semestre: todo lo que evalué o me tocaba evaluar ahí, cerrado o no.
  const revisiones = delPeriodo
    .filter((i) => i.soy_comision && (semestre ? true : i.estado === "en_evaluacion" || sinEntregar(i) || i.ya_evalue))
    // Primero las que me faltan por evaluar, después las que esperan la entrega y al
    // final las evaluadas; dentro de cada una, por nombre.
    .sort((a, b) => ordenRevision(a) - ordenRevision(b) || a.estudiante.localeCompare(b.estudiante));

  return {
    mios: filasPorEstudiante(
      delPeriodo.filter((i) => i.soy_guia || i.soy_coguia),
      semestre ? null : vigente,
    ),
    revisiones,
    // Lo que no se ha entregado aparece en la lista pero no en el número: el número
    // avisa que hay algo que hacer, y ahí todavía no lo hay.
    porRevisar: revisiones.filter(porEvaluar).length,
  };
}

/** 0 por evaluar, 1 esperando la entrega, 2 ya evaluada. */
export function ordenRevision(i: InstanciaPaso): number {
  if (i.ya_evalue) return 2;
  return sinEntregar(i) ? 1 : 0;
}
